import React, { useRef, useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LandingPageContext } from '../../contexts/LandingPageContext';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import './GallerySection.css';

gsap.registerPlugin(ScrollTrigger);

const GallerySection = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { gallerySection, getScrollSmoother } = useContext(LandingPageContext);
    const sectionRef = useRef(null);
    const galleryRef = useRef(null);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        const smoother = getScrollSmoother();
        if (!smoother) return;

        const el = sectionRef.current;
        const galleryEl = galleryRef.current;

        gsap.fromTo(el, { opacity: 0, y: 50 }, {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
                trigger: el,
                start: 'top 80%',
                scroller: smoother.scroller // Use ScrollSmoother's scroller
            }
        });

        // Animate gallery items on scroll
        const galleryItems = galleryEl ? galleryEl.children : [];
        gsap.fromTo(galleryItems, { opacity: 0, scale: 0.8 }, {
            opacity: 1,
            scale: 1,
            duration: 0.8,
            ease: 'power3.out',
            stagger: 0.1,
            scrollTrigger: {
                trigger: galleryEl,
                start: 'top 90%',
                scroller: smoother.scroller // Use ScrollSmoother's scroller
            }
        });

        return () => {
            // capture the triggers at the time of mounting and kill those that refer to our elements
            ScrollTrigger.getAll().forEach(st => {
                if (st.trigger === el || st.trigger === galleryEl) st.kill();
            });
        };
    }, [getScrollSmoother]);

    const openModal = (image) => {
        setSelectedImage(image);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    // navigate back to home page
    const goBack = () => {
        try {
            // mark that we should skip the splash when returning to home this session
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    sessionStorage.setItem('skipSplash', '1');
                }
            } catch (e) {
                // ignore storage errors
            }

            navigate('/');
        } catch (e) {
            // best-effort fallback: attempt location replace
            if (typeof window !== 'undefined') window.location.href = '/';
        }
    };

    return (
        <div id="gallerySection" className="gallery-section" ref={sectionRef}>

            {/* Back button for easier navigation - always returns to home and uses i18n */}
            <button className="gallery-back-btn" onClick={goBack} aria-label={t('gallery.back')}>
                <svg className="gallery-back-arrow" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                    <path d="M19 12H5m6 6-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="gallery-back-text">{t('gallery.back')}</span>
            </button>

            <div className="gallery-grid" ref={galleryRef}>
                {(Array.isArray(gallerySection.images) ? gallerySection.images : []).map((image, index) => (
                    <div
                        key={index}
                        className="gallery-item"
                        onClick={() => openModal(image)}
                    >
                        <img src={image} alt={`Gallery ${index + 1}`} />
                        <div className="gallery-overlay">
                            <span>{t('gallery.view')}</span>
                        </div>
                    </div>
                ))}
            </div>

            {selectedImage && (
                <div className="gallery-modal" onClick={closeModal}>
                    <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="gallery-modal-close" onClick={closeModal}>×</button>
                        <img src={selectedImage} alt="Selected" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GallerySection;

